import schema from "../schema/agent-spec.v1.json";
import type { AgentSpec } from "./types";

/**
 * Fills in every default the SDK fills in when it loads a spec (the registry stores that full
 * form), read from the agent-spec JSON Schema, factory defaults included; fields whose default
 * is null are left out. Lets the Builder tell "what I would write" from "what someone changed"
 * without tripping over defaults.
 */
type Schema = {
  $ref?: string;
  default?: unknown;
  properties?: Record<string, Schema>;
  items?: Schema;
  anyOf?: Schema[];
  oneOf?: Schema[];
  discriminator?: { propertyName: string; mapping: Record<string, string> };
  type?: string;
};

const defs = (schema as { $defs: Record<string, Schema> }).$defs;
const resolve = (s: Schema): Schema => (s.$ref ? resolve(defs[s.$ref.split("/").pop() as string] as Schema) : s);
const isObject = (v: unknown): v is Record<string, unknown> => !!v && typeof v === "object" && !Array.isArray(v);
const clone = <T>(v: T): T => (v === undefined ? v : (JSON.parse(JSON.stringify(v)) as T));

function fill(value: unknown, raw: Schema): unknown {
  const s = resolve(raw);
  if (s.oneOf && s.discriminator && isObject(value)) {
    const ref = s.discriminator.mapping[String(value[s.discriminator.propertyName])];
    return ref ? fill(value, { $ref: ref }) : value;
  }
  if (s.anyOf) {
    if (value === null || value === undefined) return value;
    const branch = s.anyOf.map(resolve).find((b) => (Array.isArray(value) ? b.type === "array" : isObject(value) ? !!b.properties || !!b.oneOf : false));
    return branch ? fill(value, branch) : value;
  }
  if (Array.isArray(value)) return s.items ? value.map((v) => fill(v, s.items as Schema)) : value;
  if (isObject(value) && s.properties) {
    const out: Record<string, unknown> = { ...value };
    for (const [key, prop] of Object.entries(s.properties)) {
      const fallback = "default" in prop ? prop.default : resolve(prop).default;
      // A field whose default is empty means the same left out or null (the SDK writes some of
      // these and leaves others out), so it is always left out.
      if (fallback === null && (value[key] === null || value[key] === undefined)) delete out[key];
      else if (key in value && value[key] !== undefined) out[key] = fill(value[key], prop);
      else if (fallback !== undefined) out[key] = fill(clone(fallback), prop);
    }
    return out;
  }
  return value;
}

export function withDefaults(spec: AgentSpec): AgentSpec {
  return fill(spec, schema as Schema) as AgentSpec;
}
